import React, { useCallback } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Select } from '@e3dc-react/shell/Elements/Select'
import { languages } from '@e3dc-react/shell/Configuration/languages'
import { makeStyles } from 'tss-react/mui'
import { AppLang } from '@e3dc-react/shell/State/App/AppModel'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import { changeAppLanguage } from 'src/Libs/languages/functions/default'
import useUserLanguage from 'src/Libs/user/hooks/use-user-language'
import { CustomStyles } from '@e3dc-react/shell/Elements/Select/FeatureRichSelect'
import { isFlowTheme } from 'src/Libs/utils'

const useStyles = makeStyles()(theme => ({
  select: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
    '& *': {
      color: theme.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        color: theme.palette.text.sidebar,
      },
    },
  },
  languages: {
    marginRight: theme.spacing(2),
    width: 12,
    height: 12,
    display: 'block',
    backgroundSize: 'cover',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    borderRadius: '12px',
  },
}))

interface Props {
  needsChangeConfirmation?: boolean
  language: AppLang
  setLanguage: (language: AppLang) => void
}

const LanguageSelector: React.FC<Props> = ({ needsChangeConfirmation = true, language, setLanguage }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const { changeUserLanguage } = useUserLanguage()

  const handleSelectLanguage = useCallback(
    (value: AppLang) => {
      setLanguage(value)
      if (!needsChangeConfirmation) {
        changeUserLanguage(
          { language: value },
          {
            onSuccess: () => {
              changeAppLanguage(value)
            },
          }
        )
      }
    },
    [setLanguage, needsChangeConfirmation, changeUserLanguage]
  )

  const customSelectStyles = {
    dropdownIndicator: {
      '& svg': {
        color: theme.palette.common.white,
      },
      [theme.breakpoints.up('md')]: {
        color: theme.palette.primary.contrastText,
      },
    },
    control: {
      color: theme.palette.primary.contrastText,
      border: 'none',
      background: 'transparent',
      outline: 'none',
      [theme.breakpoints.up('md')]: {
        padding: '0 10px',
        border: `1px solid ${theme.palette.primary.contrastText}`,
      },
      height: theme.spacing(4),
    },
    container: {
      padding: '0 !important',
      margin: '0 !important',
    },
    singleValue: {
      margin: 0,
    },
    menu: {
      background: theme.shape.languageSelector.backgroundColor,
      borderRadius: theme.shape.borderRadius,
    },
    option: {
      backgroundColor: 'transparent',
      color: isFlowTheme ? theme.palette.text.primary : theme.palette.common.white,
    },
  }

  const languageOptions = languages.map(option => {
    return {
      value: option.shortName as AppLang,
      label: (
        <Box display="flex" alignItems="center">
          <Box component="span" className={classes.languages} style={{ backgroundImage: `url(${option.icon as string})` }} />
          <Typography variant={downSm ? 'body2' : 'body1'}>{t(`common.languages.${option.translationKey}`)}</Typography>
        </Box>
      ),
    }
  })

  return (
    <Select
      className={classes.select}
      options={languageOptions}
      value={language}
      onChange={handleSelectLanguage}
      hasBorder={!downMd}
      customSelectStyles={downMd ? (customSelectStyles as CustomStyles) : undefined}
      menuPlacement={downMd ? 'top' : undefined}
      dataQa={'select-language'}
    />
  )
}

export default LanguageSelector
